import { IRecord } from 'interfaces';
import { v4 as uuidv4 } from 'uuid';

import { TranslationFunction } from 'hooks/useTypedTranslation';
import { createNonPersistentStore } from 'lib/zustand';
import { useAuthStore } from 'modules/auth/store/authStore';
import { getSDK } from 'modules/sdk/lib/sdk';
import { addToast } from 'modules/toast/stores/actions';

export type GuestListeningSessionStoreState = {
  sharedItemImportPromise: Promise<string> | null;
  sharedItemStatus: 'shared' | 'saving' | 'saved' | 'error';
  savedItemId: string | null;
};

export const useGuestListeningSessionStore = createNonPersistentStore<GuestListeningSessionStoreState>(
  () => {
    return {
      sharedItemImportPromise: null,
      sharedItemStatus: 'shared',
      savedItemId: null
    };
  },
  {
    isListeningScreenStore: true
  }
);

const importSharedItem = (item: IRecord, t: TranslationFunction) => {
  if (useGuestListeningSessionStore.getState().sharedItemImportPromise) return useGuestListeningSessionStore.getState().sharedItemImportPromise!;
  const user = useAuthStore.getState().user;

  if (!user || !user.uid) return Promise.resolve();

  const maybeImportSharedItem = async () => {
    useGuestListeningSessionStore.setState({ sharedItemStatus: 'saving' });
    const { library } = await getSDK();
    try {
      const newItemId = uuidv4();
      await library.importSharedItemForUser(item.originalCopy || item.id, user.uid, newItemId);
      addToast({
        id: 'import-started',
        title: t('File saved to your library.'),
        description: '',
        type: 'success',
        reskin: true
      });
      useGuestListeningSessionStore.setState({ sharedItemStatus: 'saved', savedItemId: newItemId });
      return newItemId;
    } catch (error) {
      addToast({
        id: 'import-failed',
        title: t('Failed to save file to your library.'),
        description: '',
        type: 'error',
        reskin: true
      });
      useGuestListeningSessionStore.setState({ sharedItemStatus: 'error' });
      return;
    }
  };

  const promise = maybeImportSharedItem();

  promise.then(() => {
    guestListeningSessionStoreActions.setSharedItemImportPromise(null);
  });

  return promise;
};

export const guestListeningSessionStoreActions = {
  importSharedItem,
  setSharedItemImportPromise: (promise: Promise<string> | null) => {
    useGuestListeningSessionStore.setState({ sharedItemImportPromise: promise });
  },
  setSharedItemStatus: (status: 'shared' | 'saving' | 'saved' | 'error') => {
    useGuestListeningSessionStore.setState({ sharedItemStatus: status });
  }
};
